import classnames from 'classnames'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Link } from '@src/quince-components/src/atoms/link'
import ProductInfoPrice from '@src/quince-components/src/atoms/productInfoPrice/ProductInfoPrice'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector'
import ReviewStars from '@src/quince-components/src/atoms/reviews/reviewStars/ReviewStars'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { THIRD_PARTY } from '@src/quince-site-common/src/helpers/productHelper'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { getIsFinalSaleProduct } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './miniPDPProductInfo.module.scss'

export const MiniPDPProductInfo = ({
  product,
  selectedOptions,
  selectedVariant,
  brandName,
  brandLink,
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  festivalPricePerUnit,
  formattedSubscriptionPrice,
  subscriptionOfferPricePerUnit,
  purchaseOption,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  selectedVariant?: BaseProductVariant
  brandName?: string
  brandLink?: string
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  festivalPricePerUnit?: number
  formattedSubscriptionPrice?: string
  subscriptionOfferPricePerUnit?: number
  purchaseOption: PurchaseType
}) => {
  const router = useRouter()

  const productLink = getProductLink(product, selectedOptions)
  const handleReviewClick = () => {
    router.push(productLink)
    pdpStateManager.setShowReviewSection({ reviewSectionInFocus: true })
    trackEvent(TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.spid?.toString()),
      },
    })
  }

  const hideSavings = product?.hideSavings ?? false
  const isFinalSaleProduct = getIsFinalSaleProduct(product, selectedOptions)
  const isVariantThirdParty = selectedVariant?.vendorType === THIRD_PARTY

  return (
    <div className={styles.productInfoContainer}>
      <div className={styles.productInfo}>
        <div>
          {brandName &&
            (brandLink ? (
              <Link href={brandLink} className={classnames(styles.brandName, styles.brandLink)}>
                {brandName}
              </Link>
            ) : (
              <div className={styles.brandName}>{brandName}</div>
            ))}
          <h1 className={styles.productTitle}>{product.title}</h1>
        </div>
        {product.productReviewSummary?.reviewCount && product.productReviewSummary?.reviewCount > 0 ? (
          <div onClick={handleReviewClick} className={styles.productRating}>
            <ReviewStars
              totalStars={5}
              fillStars={product.productReviewSummary?.averageRating || 0}
              fillIconClass={styles.reviewStarIcon}
              classNameIcon={styles.starIcon}
            />
            <p className={styles.reviewCount}>
              {`(`}
              <span>{product.productReviewSummary?.reviewCount}</span>
              {`)`}
            </p>
          </div>
        ) : null}
      </div>
      <ProductInfoPrice
        product={product}
        formattedSubscriptionPrice={formattedSubscriptionPrice}
        formattedSalePrice={formattedSalePrice}
        formattedTraditionalPrice={formattedTraditionalPrice}
        savingPercentage={savingPercentage}
        formattedFestivalOfferPrice={formattedFestivalOfferPrice}
        showDiscount={!isVariantThirdParty}
        subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
        pricePerServing={selectedVariant?.pricePerServing}
        traditionalPricePerServing={selectedVariant?.traditionalPricePerServing}
        festivalPricePerUnit={festivalPricePerUnit}
        hideSavings={hideSavings}
        showNewPdpDesignV2={false}
        isFinalSaleProduct={isFinalSaleProduct}
        purchaseOption={purchaseOption}
        updatedDesignForV2={true}
        isVariantThirdParty={isVariantThirdParty}
        selectedOptions={selectedOptions}
      />
    </div>
  )
}
